import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Check, Phone, Clock, Users, MessageSquare, Bot, X, ChevronUp, ChevronDown, Sparkles, FileText, Copy, ExternalLink, CheckCircle2, Settings, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Badge } from '../../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Twilio webhook URL - auto-detected from environment
const TWILIO_WEBHOOK_URL = `${API}/api/webhooks/twilio/whatsapp`;
const IS_PRODUCTION = !API?.includes('localhost');

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
  'Asia/Kolkata',
  'Asia/Dubai'
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function WhatsAppSettings({ actorId }) {
  const [settings, setSettings] = useState({
    whatsapp_enabled: false,
    whatsapp_phone_number: '',
    whatsapp_greeting: "Hi! I'm {name}, your AI assistant. How can I help you today?",
    whatsapp_auto_reply: true,
    whatsapp_response_delay: 1,
    whatsapp_typing_indicator: true,
    business_hours_enabled: false,
    business_hours_timezone: 'UTC',
    business_hours: {
      monday: { start: '09:00', end: '17:00' },
      tuesday: { start: '09:00', end: '17:00' },
      wednesday: { start: '09:00', end: '17:00' },
      thursday: { start: '09:00', end: '17:00' },
      friday: { start: '09:00', end: '17:00' },
      saturday: { start: '00:00', end: '00:00' },
      sunday: { start: '00:00', end: '00:00' }
    },
    out_of_hours_message: "Thank you for your message! I'm currently offline. I'll respond during business hours.",
    session_timeout_hours: 24,
    enable_human_handoff: true,
    human_handoff_keywords: ['speak to human', 'talk to person', 'human agent', 'representative'],
    no_kb_match_behavior: 'assign_to_human',
    // New AI Settings
    whatsapp_llm_model: '',
    whatsapp_fallback_models: [],
    whatsapp_system_prompt: '',
    whatsapp_rules: ''
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  useEffect(() => {
    loadSettings();
    loadAvailableModels();
  }, [actorId]);

  const loadAvailableModels = async () => {
    setLoadingModels(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/openrouter/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.models) {
        // Transform models for display with categories
        const models = response.data.models.map(model => {
          const id = model.id || "";
          let category = "Other";
          
          if (id.includes(":free")) category = "Free";
          else if (id.startsWith("openai/")) category = "OpenAI";
          else if (id.startsWith("anthropic/")) category = "Anthropic";
          else if (id.startsWith("google/")) category = "Google";
          else if (id.startsWith("deepseek/")) category = "DeepSeek";
          else if (id.startsWith("meta-llama/") || id.startsWith("meta/")) category = "Meta";
          else if (id.startsWith("mistralai/") || id.startsWith("mistral/")) category = "Mistral";
          else if (id.startsWith("qwen/")) category = "Qwen";
          
          return {
            value: id,
            label: model.name || id,
            category
          };
        });
        setAvailableModels(models);
      }
    } catch (err) {
      console.error('Error loading models:', err);
      toast.error('Failed to load models');
    } finally {
      setLoadingModels(false);
    }
  };

  // Helper to get models by category
  const getModelsByCategory = (category) => {
    return availableModels.filter(m => m.category === category);
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = `${API}/api/ai/actors/${actorId}/whatsapp-settings`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const loadedSettings = {
        ...settings,
        ...response.data,
        business_hours: response.data.business_hours || settings.business_hours,
        whatsapp_greeting: response.data.whatsapp_greeting || settings.whatsapp_greeting,
        out_of_hours_message: response.data.out_of_hours_message || settings.out_of_hours_message,
        human_handoff_keywords: response.data.human_handoff_keywords || settings.human_handoff_keywords,
        whatsapp_llm_model: response.data.whatsapp_llm_model || '',
        whatsapp_fallback_models: response.data.whatsapp_fallback_models || [],
        whatsapp_system_prompt: response.data.whatsapp_system_prompt || '',
        whatsapp_rules: response.data.whatsapp_rules || ''
      };
      
      setSettings(loadedSettings);
      setError(null);
    } catch (err) {
      console.error('Error loading WhatsApp settings:', err);
      setError('Failed to load WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      const token = localStorage.getItem('token');
      const url = `${API}/api/ai/actors/${actorId}/whatsapp-settings`;
      
      await axios.put(url, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving WhatsApp settings:', err);
      setError('Failed to save WhatsApp settings');
    } finally {
      setSaving(false);
    }
  };

  const handleBusinessHoursChange = (day, field, value) => {
    setSettings({
      ...settings,
      business_hours: {
        ...settings.business_hours,
        [day]: {
          ...settings.business_hours[day],
          [field]: value
        }
      }
    });
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !settings.human_handoff_keywords.includes(keywordInput.trim())) {
      setSettings({
        ...settings,
        human_handoff_keywords: [...settings.human_handoff_keywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setSettings({
      ...settings,
      human_handoff_keywords: settings.human_handoff_keywords.filter(k => k !== keyword)
    });
  };

  const addFallbackModel = (model) => {
    if (model && !settings.whatsapp_fallback_models.includes(model) && model !== settings.whatsapp_llm_model) {
      setSettings({
        ...settings,
        whatsapp_fallback_models: [...settings.whatsapp_fallback_models, model]
      });
    }
  };

  const removeFallbackModel = (model) => {
    setSettings({
      ...settings,
      whatsapp_fallback_models: settings.whatsapp_fallback_models.filter(m => m !== model)
    });
  };

  const moveFallbackModel = (index, direction) => {
    const newModels = [...settings.whatsapp_fallback_models];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newModels.length) {
      [newModels[index], newModels[newIndex]] = [newModels[newIndex], newModels[index]];
      setSettings({ ...settings, whatsapp_fallback_models: newModels });
    }
  };

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(TWILIO_WEBHOOK_URL);
      toast.success('Webhook URL copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = TWILIO_WEBHOOK_URL;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Webhook URL copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="bg-green-50 text-green-800 border-green-200">
          <Check className="h-4 w-4" />
          <AlertDescription>WhatsApp settings saved successfully!</AlertDescription>
        </Alert>
      )}

      {/* Twilio Webhook Configuration */}
      <Card className={IS_PRODUCTION ? "border-green-200" : "border-amber-200"}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Twilio Webhook Configuration
              </CardTitle>
              <CardDescription>Configure this URL in your Twilio Console for WhatsApp messaging</CardDescription>
            </div>
            {IS_PRODUCTION ? (
              <Badge className="bg-green-100 text-green-700">Production Ready</Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700">Development</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Webhook URL</Label>
            <div className="mt-1 flex items-center gap-2">
              <code className={`flex-1 p-3 rounded-lg text-sm break-all ${IS_PRODUCTION ? "bg-green-50 text-green-800 border border-green-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
                {TWILIO_WEBHOOK_URL}
              </code>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={copyWebhookUrl}
                className="flex-shrink-0"
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </Button>
            </div>
            {IS_PRODUCTION ? (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Production URL detected - ready to use in Twilio Console
              </p>
            ) : (
              <p className="mt-2 text-sm text-amber-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Localhost URL detected - this won't work in production. Deploy your app first.
              </p>
            )}
          </div>

          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <p className="font-medium text-sm text-slate-700">Setup Instructions:</p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600">
              <li>Go to <a href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">Twilio Console <ExternalLink className="w-3 h-3" /></a></li>
              <li>Navigate to <strong>Messaging → Settings → WhatsApp Sandbox Settings</strong> (for testing) or your WhatsApp Sender configuration (for production)</li>
              <li>In the <strong>"When a message comes in"</strong> field, paste the webhook URL above</li>
              <li>Set the HTTP method to <strong>POST</strong></li>
              <li>Save your changes</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Basic Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            WhatsApp Configuration
          </CardTitle>
          <CardDescription>Configure WhatsApp messaging for this AI Actor</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="whatsapp_enabled">Enable WhatsApp</Label>
              <p className="text-sm text-muted-foreground">Allow this actor to receive and respond to WhatsApp messages</p>
            </div>
            <Switch
              id="whatsapp_enabled"
              checked={settings.whatsapp_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, whatsapp_enabled: checked })}
            />
          </div>

          {settings.whatsapp_enabled && (
            <>
              <div>
                <Label htmlFor="whatsapp_phone_number">
                  WhatsApp Phone Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="whatsapp_phone_number"
                  type="tel"
                  placeholder="+1234567890"
                  value={settings.whatsapp_phone_number || ''}
                  onChange={(e) => setSettings({ ...settings, whatsapp_phone_number: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Include country code (e.g., +1 for US). Must match your Twilio WhatsApp number.
                </p>
              </div>

              <div>
                <Label htmlFor="whatsapp_greeting">Greeting Message</Label>
                <Textarea
                  id="whatsapp_greeting"
                  rows={3}
                  placeholder="Hi! I'm {name}, your AI assistant. How can I help you today?"
                  value={settings.whatsapp_greeting || ''}
                  onChange={(e) => setSettings({ ...settings, whatsapp_greeting: e.target.value })}
                  className="mt-1"
                  maxLength={1600}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use {'{name}'} to insert actor name. Character limit: {settings.whatsapp_greeting?.length || 0}/1600
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="whatsapp_auto_reply">Auto-Reply</Label>
                    <p className="text-xs text-muted-foreground">Automatically respond to messages</p>
                  </div>
                  <Switch
                    id="whatsapp_auto_reply"
                    checked={settings.whatsapp_auto_reply}
                    onCheckedChange={(checked) => setSettings({ ...settings, whatsapp_auto_reply: checked })}
                  />
                </div>

                <div>
                  <Label htmlFor="whatsapp_response_delay">Response Delay (seconds)</Label>
                  <Select
                    value={String(settings.whatsapp_response_delay)}
                    onValueChange={(value) => setSettings({ ...settings, whatsapp_response_delay: parseInt(value) })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Immediate</SelectItem>
                      <SelectItem value="1">1 second</SelectItem>
                      <SelectItem value="2">2 seconds</SelectItem>
                      <SelectItem value="3">3 seconds</SelectItem>
                      <SelectItem value="5">5 seconds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* AI Model Configuration - NEW */}
      {settings.whatsapp_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              AI Model Configuration
            </CardTitle>
            <CardDescription>Configure which AI model to use for WhatsApp conversations</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="whatsapp_llm_model">Primary AI Model</Label>
              <p className="text-xs text-muted-foreground mb-2">Select a specific model for WhatsApp, or leave empty to use the actor&apos;s default model</p>
              <Select
                value={settings.whatsapp_llm_model || '__default__'}
                onValueChange={(value) => setSettings({ ...settings, whatsapp_llm_model: value === '__default__' ? '' : value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Use Actor Default" />
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  <SelectItem value="__default__">Use Actor Default</SelectItem>
                  
                  {loadingModels ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      <span className="text-sm text-slate-500">Loading models...</span>
                    </div>
                  ) : availableModels.length > 0 ? (
                    <>
                      {/* Free Models */}
                      {getModelsByCategory("Free").length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-green-700 bg-green-50">
                            🆓 Free Models
                          </div>
                          {getModelsByCategory("Free").map(model => (
                            <SelectItem key={model.value} value={model.value}>
                              <span className="flex items-center gap-2">
                                {model.label}
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">Free</Badge>
                              </span>
                            </SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* OpenAI Models */}
                      {getModelsByCategory("OpenAI").length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                            OpenAI
                          </div>
                          {getModelsByCategory("OpenAI").slice(0, 10).map(model => (
                            <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Anthropic Models */}
                      {getModelsByCategory("Anthropic").length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                            Anthropic
                          </div>
                          {getModelsByCategory("Anthropic").slice(0, 10).map(model => (
                            <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Google Models */}
                      {getModelsByCategory("Google").length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                            Google
                          </div>
                          {getModelsByCategory("Google").slice(0, 10).map(model => (
                            <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* DeepSeek Models */}
                      {getModelsByCategory("DeepSeek").length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                            DeepSeek
                          </div>
                          {getModelsByCategory("DeepSeek").slice(0, 10).map(model => (
                            <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Meta Models */}
                      {getModelsByCategory("Meta").length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                            Meta Llama
                          </div>
                          {getModelsByCategory("Meta").slice(0, 10).map(model => (
                            <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Mistral Models */}
                      {getModelsByCategory("Mistral").length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                            Mistral
                          </div>
                          {getModelsByCategory("Mistral").slice(0, 10).map(model => (
                            <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Qwen Models */}
                      {getModelsByCategory("Qwen").length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                            Qwen
                          </div>
                          {getModelsByCategory("Qwen").slice(0, 10).map(model => (
                            <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="px-2 py-4 text-center text-sm text-slate-500">
                      No models available
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Fallback Models</Label>
              <p className="text-xs text-muted-foreground mb-2">
                If the primary model fails, these models will be tried in order
              </p>
              
              {/* Current fallback chain */}
              <div className="space-y-2 mb-3">
                {settings.whatsapp_fallback_models && settings.whatsapp_fallback_models.length > 0 ? (
                  settings.whatsapp_fallback_models.map((model, index) => (
                    <div key={model} className="flex items-center gap-2 bg-slate-50 p-2 rounded-md">
                      <Badge variant="outline" className="font-mono text-xs">
                        #{index + 1}
                      </Badge>
                      <span className="flex-1 text-sm truncate">{model}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveFallbackModel(index, -1)}
                          disabled={index === 0}
                          className="h-6 w-6 p-0"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveFallbackModel(index, 1)}
                          disabled={index === settings.whatsapp_fallback_models.length - 1}
                          className="h-6 w-6 p-0"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFallbackModel(model)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    No fallback models configured. Add models below.
                  </p>
                )}
              </div>

              {/* Add new fallback model */}
              {settings.whatsapp_fallback_models && settings.whatsapp_fallback_models.length < 5 && (
                <Select
                  value="__placeholder__"
                  onValueChange={(value) => value !== '__placeholder__' && addFallbackModel(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Add fallback model..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="__placeholder__" disabled>Add fallback model...</SelectItem>
                    {availableModels.filter(m => 
                      m.value && 
                      m.value !== '__default__' &&
                      m.value !== settings.whatsapp_llm_model && 
                      !settings.whatsapp_fallback_models.includes(m.value)
                    ).slice(0, 50).map(model => (
                      <SelectItem key={model.value} value={model.value}>
                        <span className="flex items-center gap-2">
                          {model.label}
                          {model.category === "Free" && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">Free</Badge>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Prompt & Rules - NEW */}
      {settings.whatsapp_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              AI Behavior Configuration
            </CardTitle>
            <CardDescription>Customize how the AI responds in WhatsApp conversations</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="whatsapp_system_prompt">WhatsApp System Prompt</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Custom instructions for the AI when handling WhatsApp messages. Leave empty to use the actor&apos;s default system prompt.
              </p>
              <Textarea
                id="whatsapp_system_prompt"
                rows={4}
                placeholder="You are a friendly customer support assistant. Keep responses concise and helpful..."
                value={settings.whatsapp_system_prompt || ''}
                onChange={(e) => setSettings({ ...settings, whatsapp_system_prompt: e.target.value })}
                className="font-mono text-sm"
              />
            </div>

            <div>
              <Label htmlFor="whatsapp_rules">Response Rules & Guidelines</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Additional rules the AI must follow in WhatsApp conversations (e.g., tone, formatting, restrictions)
              </p>
              <Textarea
                id="whatsapp_rules"
                rows={4}
                placeholder="- Always greet customers warmly&#10;- Keep responses under 300 characters when possible&#10;- Never share pricing without approval&#10;- Escalate complaints to human agents"
                value={settings.whatsapp_rules || ''}
                onChange={(e) => setSettings({ ...settings, whatsapp_rules: e.target.value })}
                className="font-mono text-sm"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Business Hours */}
      {settings.whatsapp_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Business Hours
            </CardTitle>
            <CardDescription>Set availability schedule for auto-replies</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="business_hours_enabled">Enable Business Hours</Label>
                <p className="text-sm text-muted-foreground">Only respond during specified hours</p>
              </div>
              <Switch
                id="business_hours_enabled"
                checked={settings.business_hours_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, business_hours_enabled: checked })}
              />
            </div>

            {settings.business_hours_enabled && (
              <>
                <div>
                  <Label htmlFor="business_hours_timezone">Timezone</Label>
                  <Select
                    value={settings.business_hours_timezone}
                    onValueChange={(value) => setSettings({ ...settings, business_hours_timezone: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Weekly Schedule</Label>
                  <div className="space-y-2">
                    {DAYS.map(day => (
                      <div key={day} className="grid grid-cols-3 gap-2 items-center">
                        <Label className="capitalize">{day}</Label>
                        <Input
                          type="time"
                          value={settings.business_hours?.[day]?.start || '09:00'}
                          onChange={(e) => handleBusinessHoursChange(day, 'start', e.target.value)}
                        />
                        <Input
                          type="time"
                          value={settings.business_hours?.[day]?.end || '17:00'}
                          onChange={(e) => handleBusinessHoursChange(day, 'end', e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Set both to 00:00 to mark as closed</p>
                </div>

                <div>
                  <Label htmlFor="out_of_hours_message">Out of Hours Message</Label>
                  <Textarea
                    id="out_of_hours_message"
                    rows={2}
                    value={settings.out_of_hours_message || ''}
                    onChange={(e) => setSettings({ ...settings, out_of_hours_message: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Human Handoff */}
      {settings.whatsapp_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Human Handoff
            </CardTitle>
            <CardDescription>Configure when to transfer conversations to human agents</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enable_human_handoff">Enable Human Handoff</Label>
                <p className="text-sm text-muted-foreground">Allow customers to request human assistance</p>
              </div>
              <Switch
                id="enable_human_handoff"
                checked={settings.enable_human_handoff}
                onCheckedChange={(checked) => setSettings({ ...settings, enable_human_handoff: checked })}
              />
            </div>

            {settings.enable_human_handoff && (
              <>
                <div>
                  <Label>Handoff Keywords</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Messages containing these phrases will trigger human handoff
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {settings.human_handoff_keywords?.map(keyword => (
                      <Badge key={keyword} variant="secondary" className="flex items-center gap-1">
                        {keyword}
                        <button
                          onClick={() => removeKeyword(keyword)}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add keyword..."
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                    />
                    <Button onClick={addKeyword} variant="outline" size="sm">Add</Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="no_kb_match_behavior">When No Knowledge Base Match</Label>
                  <Select
                    value={settings.no_kb_match_behavior}
                    onValueChange={(value) => setSettings({ ...settings, no_kb_match_behavior: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="assign_to_human">Assign to Human Agent</SelectItem>
                      <SelectItem value="generic_response">Provide Generic Response</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    What to do when the AI cannot find relevant information in the knowledge base
                  </p>
                </div>

                <div>
                  <Label htmlFor="session_timeout_hours">Session Timeout (hours)</Label>
                  <Input
                    id="session_timeout_hours"
                    type="number"
                    min="1"
                    max="168"
                    value={settings.session_timeout_hours}
                    onChange={(e) => setSettings({ ...settings, session_timeout_hours: parseInt(e.target.value) })}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Conversations expire after this many hours of inactivity
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save WhatsApp Settings'}
        </Button>
      </div>
    </div>
  );
}
